import { useState, useEffect, useCallback } from "react";
import { getAdminUsers, updateAdminUser } from "../../lib/api";
import { toList } from "../../lib/apiAdminUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const PLANS = ["free", "pro", "clinic"];
const PLAN_LABELS = { free: "Gratis", pro: "Pro", clinic: "Clínica" };
const PLAN_COLORS = { free: "bg-sky-100 text-sky-800", pro: "bg-emerald-100 text-emerald-800", clinic: "bg-violet-100 text-violet-800" };
const ROLE_LABELS = { admin: "Admin", founder: "Fundador", free: "Gratis", paid: "Pagado", trial: "Prueba" };
const ROLE_COLORS = { admin: "bg-violet-100", founder: "bg-violet-100", free: "bg-sky-100", paid: "bg-emerald-100", trial: "bg-amber-100" };
const SUB_LABELS = { trial: "Prueba", active: "Activo", past_due: "Vencido", cancelled: "Cancelado", expired: "Expirado", free: "Gratis" };
const SUB_COLORS = { trial: "bg-amber-100", active: "bg-emerald-100", past_due: "bg-red-100", cancelled: "bg-slate-100", expired: "bg-red-100", free: "bg-sky-100" };
const MARKET_LABELS = { latam: "LATAM", usa: "USA", unknown: "Sin clasificar" };
const MODE_LABELS = { live: "Live", test: "Test", unknown: "-" };

function formatDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

function displayPlan(u) {
  const p = (u.plan || "").toLowerCase();
  const r = (u.billing_role || "").toLowerCase();
  if (r === "free") return "free";
  if (r === "admin" || r === "founder") return r;
  if (p === "pro" || p === "premium") return "pro";
  if (p === "clinic") return "clinic";
  return p || "lite";
}

function externalBillingId(u) {
  return u.lemon_subscription_id || u.stripe_subscription_id || u.paddle_subscription_id || "-";
}

function externalCustomerId(u) {
  return u.lemon_customer_id || u.stripe_customer_id || u.paddle_customer_id || "-";
}

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "-";
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency || ""}`.trim();
  const normalized = n > 1000 ? n / 100 : n;
  const code = (currency || "").toUpperCase();
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: code || "USD",
      maximumFractionDigits: 2,
    }).format(normalized);
  } catch {
    return `${normalized.toFixed(2)} ${code || ""}`.trim();
  }
}

export default function AdminBilling() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [marketFilter, setMarketFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers();
      setUsers(toList(res?.data));
    } catch (err) {
      setUsers([]);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((u) => {
    const dp = displayPlan(u);
    const sub = (u.subscription_status || "").toLowerCase();
    const market = (u.billing_market || "unknown").toLowerCase();
    const mode = (u.billing_mode || "unknown").toLowerCase();
    if (planFilter && planFilter !== "all" && dp !== planFilter) return false;
    if (statusFilter && statusFilter !== "all" && sub !== statusFilter) return false;
    if (marketFilter && marketFilter !== "all" && market !== marketFilter) return false;
    if (modeFilter && modeFilter !== "all" && mode !== modeFilter) return false;
    return true;
  });

  const quickAction = async (userId, action) => {
    try {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      const nextPeriod = d.toISOString().slice(0, 10);
      switch (action) {
        case "extend_trial":
          await updateAdminUser(userId, { billing_role: "trial", subscription_status: "trial", trial_ends_at: nextPeriod, plan: "pro" });
          break;
        case "convert_free":
          await updateAdminUser(userId, { plan: "free", billing_role: "free", subscription_status: "free" });
          break;
        case "upgrade_pro":
          await updateAdminUser(userId, { plan: "pro", billing_role: "paid", subscription_status: "active", current_period_end: nextPeriod });
          break;
        case "assign_clinic":
          await updateAdminUser(userId, { plan: "clinic", billing_role: "paid", subscription_status: "active", current_period_end: nextPeriod });
          break;
        case "cancel":
          await updateAdminUser(userId, { subscription_status: "cancelled" });
          break;
        case "reactivate":
          await updateAdminUser(userId, { subscription_status: "active", current_period_end: nextPeriod });
          break;
        default:
          return;
      }
      toast.success("Acción aplicada");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Facturación / Suscripciones</h1>
        <p className="text-slate-500 mt-1">Gestión de planes y estados de suscripción</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <CardTitle className="text-lg">Suscripciones</CardTitle>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Plan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {PLANS.map((p) => <SelectItem key={p} value={p}>{PLAN_LABELS[p]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {["trial","active","past_due","cancelled","expired","free"].map((s) => <SelectItem key={s} value={s}>{SUB_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={marketFilter} onValueChange={setMarketFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Mercado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="latam">LATAM</SelectItem>
                <SelectItem value="usa">USA</SelectItem>
                <SelectItem value="unknown">Sin clasificar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="Modo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="unknown">-</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="py-12 text-center text-slate-500">Cargando...</div> :
           filteredUsers.length === 0 ? <div className="py-12 text-center text-slate-500">No hay usuarios</div> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Mercado</TableHead>
                    <TableHead>Modo</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Importe</TableHead>
                    <TableHead>Trial hasta</TableHead>
                    <TableHead>Periodo hasta</TableHead>
                    <TableHead>Sub ID</TableHead>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Variant ID</TableHead>
                    <TableHead>Product ID</TableHead>
                    <TableHead>Registro</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => {
                    const dp = displayPlan(u);
                    const sub = (u.subscription_status || "").toLowerCase();
                    const market = (u.billing_market || "unknown").toLowerCase();
                    const mode = (u.billing_mode || "unknown").toLowerCase();
                    const provider = (u.billing_provider || "unknown").toLowerCase();
                    return (
                      <TableRow key={u.id} className={u.is_active === false ? "opacity-60" : ""}>
                        <TableCell className="font-medium">{u.name || u.nombre || u.email}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell><Badge variant="outline" className={PLAN_COLORS[dp] || ""}>{PLAN_LABELS[dp] || dp}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className={ROLE_COLORS[(u.billing_role || "").toLowerCase()] || ""}>{ROLE_LABELS[(u.billing_role || "").toLowerCase()] || u.billing_role}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className={SUB_COLORS[sub] || ""}>{SUB_LABELS[sub] || sub || "-"}</Badge></TableCell>
                        <TableCell>{MARKET_LABELS[market] || market}</TableCell>
                        <TableCell>{MODE_LABELS[mode] || mode}</TableCell>
                        <TableCell className="uppercase text-xs">{provider}</TableCell>
                        <TableCell className="text-sm">{formatMoney(u.amount, u.currency)}</TableCell>
                        <TableCell className="text-sm">{formatDate(u.trial_ends_at)}</TableCell>
                        <TableCell className="text-sm">{formatDate(u.current_period_end)}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono max-w-[120px] truncate" title={externalBillingId(u)}>{externalBillingId(u)}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono max-w-[120px] truncate" title={externalCustomerId(u)}>{externalCustomerId(u)}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono max-w-[120px] truncate" title={u.lemon_variant_id || "-"}>{u.lemon_variant_id || "-"}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono max-w-[120px] truncate" title={u.lemon_product_id || "-"}>{u.lemon_product_id || "-"}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => quickAction(u.id, "extend_trial")}>Extender prueba 30 días</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => quickAction(u.id, "convert_free")}>Convertir a gratuito</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => quickAction(u.id, "upgrade_pro")}>Actualizar a Pro</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => quickAction(u.id, "assign_clinic")}>Asignar Clínica</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => quickAction(u.id, "cancel")} className="text-amber-700">Cancelar suscripción</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => quickAction(u.id, "reactivate")}>Reactivar suscripción</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
